<script lang="ts" setup>
import { createOtTableColumnInstance, otTableSortDirections, createFlexTableColumn } from '@1trading/design-system';
import { groupBy, isEmpty, forEach, orderBy, pipe } from 'lodash/fp';
import { computed, type PropType, ref, watch, h } from 'vue';
import { useI18n } from 'vue-i18n';

import BigNumber from '@exchange/helpers/bignumber';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { type MarketModel, MarketState } from '@exchange/libs/market/service/src';
import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import type { CurrencyModel } from '@exchange/libs/utils/currency/src';
import { currencyService } from '@exchange/libs/utils/currency/src';

import MarketListItem from './MarketListItem.vue';

const props = defineProps({
  favoritesIdList: { type: Array as PropType<Array<string>> },
  filteredBy: { type: String },
  isFavorite: { type: Function as PropType<(id: string) => boolean>, required: true },
  isFilteredByFavorites: { type: Boolean, required: true },
  listItemClass: { type: String, required: false },
  marketId: { type: String, required: true },
  marketList: { type: Array as PropType<Array<MarketModel>> },
  updateFavorite: { type: Function as PropType<(marketId: string) => Promise<void>>, required: true },
  withLogo: { type: Boolean, required: true },
});

const emit = defineEmits<{
  (e: 'list:first-sorting-done'): void;
  (e: 'list:market-clicked', marketId: string): void;
}>();

const { t } = useI18n({ useScope: 'global' });
const { isXS } = useMQBreakpoints();

const getFiatVolume = (market: MarketModel, currencies: Record<string, CurrencyModel | undefined>) => {
  const defaultFiatValue = currencies[CONSTANTS.DEFAULT_FOR_CURRENCY_FORMATTING]?.defaultFiatValue;

  if (!market.allDay.volume || !currencies || defaultFiatValue === undefined) {
    return Number.NaN;
  }

  return new BigNumber(market.allDay.globalVolume).times(defaultFiatValue).toNumber();
};

const sortMarketsByFiatVolume = (currencies: Record<string, CurrencyModel | undefined>, markets: Array<MarketModel>) => {
  const sortF = pipe(
    forEach((market: MarketModel) => {
      // eslint-disable-next-line no-param-reassign
      market.fiatVolume = getFiatVolume(market, currencies);
    }),
    orderBy(
      [
        (market) => (market.type === MarketType.PERP ? 0 : 1), // PERP first
        ({ fiatVolume }) => fiatVolume || 0,
      ],
      ['asc', 'desc'],
    ),
  );

  return sortF(markets);
};

const finiteSort = (a: number, b: number) => {
  if (Number.isFinite(a - b)) {
    return a - b;
  }

  return Number.isFinite(a) ? 1 : -1;
};

const { currencies } = currencyService;

const firstSortingDone = ref(false);
const canSort = computed(() => !isEmpty(currencies.value) && (!isEmpty(props.marketList) || (props.isFilteredByFavorites && isEmpty(props.favoritesIdList))));
const internalMarketList = computed<Array<MarketModel>>(() => {
  if (!props.marketList || !currencies.value) {
    return [];
  }

  const volumeSorted = sortMarketsByFiatVolume(currencies.value, props.marketList);

  const groupByNewStatus = groupBy((market: MarketModel) => market.getMarketUiState() === MarketState.NEW, volumeSorted);

  return [...(groupByNewStatus.true ?? []), ...(groupByNewStatus.false ?? [])];
});

const getColumns = (sortByVolume: (marketA: MarketModel, marketB: MarketModel) => number) => {
  const defineSorting = (fn: (marketA: MarketModel, marketB: MarketModel) => number) => {
    if (isXS.value) {
      return undefined;
    }

    return fn;
  };

  const favoriteCol = computed(() =>
    createOtTableColumnInstance({
      title: '',
      columnClass: 'favorite-column-header',
    }),
  );
  const marketCol = computed(() =>
    createOtTableColumnInstance({
      title: t('modules.marketList.header.market'),
      columnClass: 'text-align-left market-column-header market-menu-list__table-column',
      titleClass: 'market-menu-list__table-title',
      sortFunction: defineSorting((a, b) => (a.name > b.name ? 1 : -1)),
    }),
  );
  const priceChangeCol = computed(() =>
    createOtTableColumnInstance({
      title: t('modules.marketList.header.priceChange'),
      columnClass: 'text-align-right price-column-header market-menu-list__table-column',
      titleClass: 'market-menu-list__table-title',
    }),
  );
  const priceCol = computed(() =>
    createOtTableColumnInstance({
      title: t('modules.marketList.header.price'),
      columnClass: 'text-align-right price-column-header market-menu-list__table-column',
      titleClass: 'market-menu-list__table-title',
      sortFunction: defineSorting((a, b) => finiteSort(a.lastPrice, b.lastPrice)),
    }),
  );
  const volumeCol = computed(() =>
    createOtTableColumnInstance({
      title: t('modules.marketList.header.volume'),
      columnClass: 'text-align-right volume-column-header market-menu-list__table-column',
      titleClass: 'market-menu-list__table-title',
      sortFunction: defineSorting(sortByVolume),
      sortDirection: otTableSortDirections.desc,
    }),
  );
  const changeCol = computed(() =>
    createOtTableColumnInstance({
      title: t('modules.marketList.header.change'),
      columnClass: 'text-align-right change-column-header market-menu-list__table-column',
      titleClass: 'market-menu-list__table-title',
      sortFunction: defineSorting((a, b) => finiteSort(a.allDay.priceChangePercentage, b.allDay.priceChangePercentage)),
    }),
  );

  return {
    favoriteCol,
    marketCol,
    priceChangeCol,
    priceCol,
    volumeCol,
    changeCol,
  };
};

const sortByVolume = (marketA: MarketModel, marketB: MarketModel) => {
  if (!currencies.value) {
    return 1;
  }

  return finiteSort(getFiatVolume(marketA, currencies.value), getFiatVolume(marketB, currencies.value));
};
const { favoriteCol, marketCol, priceChangeCol, priceCol, volumeCol, changeCol } = getColumns(sortByVolume);

const header = computed(() =>
  isXS.value ? [favoriteCol.value, marketCol.value, priceChangeCol.value] : [favoriteCol.value, marketCol.value, priceCol.value, volumeCol.value, changeCol.value],
);

const emitFirstSortingDone = () => {
  if (firstSortingDone.value) {
    emit('list:first-sorting-done');
    firstSortingDone.value = true;
  }
};

const changeMarket = (marketId: string) => {
  emit('list:market-clicked', marketId);
};

watch(
  () => props.marketList,
  () => {
    if (canSort.value) {
      emitFirstSortingDone();
    }
  },
  { immediate: true },
);

watch(canSort, (value) => {
  if (value && !firstSortingDone.value) {
    emitFirstSortingDone();
  }
});
</script>

<template>
  <ot-table
    :class="['text-align-left market-list', { 'market-list--with-logo': withLogo }]"
    :has-borders="true"
    :header="header"
    :rows="internalMarketList"
  >
    <template #default="{ rows, rowClass }">
      <market-list-item
        v-for="market in rows"
        :key="market.id"
        class="market-list__item"
        :class="[rowClass, listItemClass]"
        :filtered-by="filteredBy"
        :is-active="marketId === market.id"
        :is-favorite="isFavorite(market.id)"
        :is-futures="market.type === MarketType.PERP"
        :market="market"
        :with-logo="withLogo"
        @item:favorite-clicked="updateFavorite"
        @item:market-clicked="changeMarket"
      />
    </template>
  </ot-table>
</template>

<style lang="scss">
.market-list {
  --market-list-favorite-width: 20px;
  --market-list-market-width: 80px;
  --market-list-price-width: 80px;
  --market-list-volume-width: 40px;
  --market-list-change-width: 40px;
  --market-list-market-padding-left: 0;

  width: 100%;

  thead {
    border: 0;
  }

  &__volume-header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
  }

  &__info-icon {
    width: 16px;
    height: 16px;
    color: rgb(var(--v-theme-text-secondary));
    cursor: help;
  }

  th.favorite-column-header {
    width: var(--market-list-favorite-width);
  }

  th.market-column-header {
    width: var(--market-list-market-width);
    padding-left: var(--market-list-market-padding-left);
  }

  th.price-column-header {
    width: var(--market-list-price-width);
  }

  th.volume-column-header {
    width: var(--market-list-volume-width);
  }

  th.change-column-header {
    width: var(--market-list-change-width);
  }
}

.market-list--with-logo {
  --market-list-market-padding-left: 20px;
}
</style>
